import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Box, Button, IconButton, Typography } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { TextFieldWithLabel } from 'Components/TextField'
import * as AppRoutes from 'Pages/Routes'
import { sendGet, sendPost } from 'hooks/use-fetch'

// Mirrors the rule shown to the user on the ChangePassword screen (client/src/Pages/ChangePassword),
// kept in sync for a consistent password-setting experience across the app - MiCredentials is still
// the actual source of truth for the password policy, but it doesn't report back why an update
// failed, so this is the only way to give the user a specific, actionable message.
function passwordMeetsRequirements (password: string): boolean {
  const containsNumber = /\d/.test(password)
  const containsUpperCaseLetter = /[A-Z]/.test(password)
  const containsLowerCaseLetter = /[a-z]/.test(password)
  return password.length >= 8 && containsNumber && containsUpperCaseLetter && containsLowerCaseLetter
}

type KeyStatus = 'checking' | 'valid' | 'invalid'

export function ResetPasswordPage (): JSX.Element {
  const [searchParams] = useSearchParams()
  const resetKey = searchParams.get('key') ?? ''

  const [keyStatus, setKeyStatus] = useState<KeyStatus>('checking')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState<boolean>(false)

  useEffect(() => {
    const checkKey = async (): Promise<void> => {
      if (resetKey === '') {
        setKeyStatus('invalid')
        return
      }
      const { response, success } = await sendGet(`/Account/ValidatePasswordResetKey?resetKey=${encodeURIComponent(resetKey)}`, {})
      setKeyStatus(success && response === true ? 'valid' : 'invalid')
    }
    void checkKey()
  }, [resetKey])

  const submit = async (): Promise<void> => {
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match.')
      return
    }
    if (!passwordMeetsRequirements(newPassword)) {
      setValidationError('Your password does not meet the requirements below.')
      return
    }

    setValidationError(null)
    const { success, response } = await sendPost('/Account/ResetPassword', { resetKey, newPassword })
    if (success) {
      setSubmitted(true)
    } else if (typeof response === 'string' && response.includes('requirements')) {
      // Defense-in-depth: the server re-checks the password rules in case this request didn't
      // come through the form's own check above. Under normal use this branch is unreachable.
      setValidationError(response)
    } else {
      setKeyStatus('invalid')
    }
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Box sx={{ width: '100%', maxWidth: '480px', textAlign: 'center' }}>
        {keyStatus === 'checking' && <Typography sx={{ color: 'text.secondary' }}>Checking your link&hellip;</Typography>}

        {keyStatus === 'invalid' && (
          <>
            <Typography variant="h4" component="h1" sx={{ mb: 2 }}>This link is invalid</Typography>
            <Typography sx={{ color: 'text.secondary', mb: 3 }} data-testid="reset-password-invalid">
              This password reset link is invalid or has expired. Links are only valid for 15 minutes and can only be used once.
            </Typography>
            <Link to={AppRoutes.FORGOT_PASSWORD}>Request a new link</Link>
            <Box sx={{ mt: 1.5 }}>
              <Link to={AppRoutes.LOGIN}>Return to login</Link>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              {/* Same service desk link as the app-wide footer (Components/Footer) */}
              <a
                href="https://oaklandschools.atlassian.net/helpcenter/miservicedesk/portal/71/group/117/create/153"
                target="_blank"
                rel="noreferrer"
                data-testid="reset-password-support-link"
              >
                Still having trouble?
              </a>
            </Box>
          </>
        )}

        {keyStatus === 'valid' && (
          submitted
            ? (
              <>
                <Typography variant="h4" component="h1" sx={{ mb: 2 }}>Password updated</Typography>
                <Typography sx={{ color: 'text.secondary', mb: 3 }} data-testid="reset-password-confirmation">
                  Your password has been reset. You can now sign in with your new password.
                </Typography>
                <Link to={AppRoutes.LOGIN}>Return to login</Link>
              </>
              )
            : (
              <form noValidate onSubmit={event => { event.preventDefault(); void submit() }}>
                <Typography variant="h4" component="h1" sx={{ mb: 1 }}>Set a new password</Typography>
                <Typography sx={{ color: 'text.secondary', mb: 3 }}>
                  Enter a new password for your account.
                </Typography>

                <TextFieldWithLabel
                  label="New Password"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={event => { setNewPassword(event.target.value) }}
                  maxWidth="100%"
                  dataTestId="reset-password-new"
                  endAdornment={
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => { setShowPassword(!showPassword) }}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                />

                <TextFieldWithLabel
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={event => { setConfirmPassword(event.target.value) }}
                  maxWidth="100%"
                  dataTestId="reset-password-confirm"
                />

                {validationError != null && (
                  <Typography sx={{ color: 'error.main', mt: 1 }} data-testid="reset-password-validation-error">
                    {validationError}
                  </Typography>
                )}

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    textAlign: 'left',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Password requirements:</Typography>
                  <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>At least 8 characters</li>
                    <li>An uppercase and a lowercase letter</li>
                    <li>A number</li>
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ textTransform: 'none', mt: 3 }}
                  data-testid="reset-password-submit"
                >
                  Reset Password
                </Button>

                <Box sx={{ mt: 2 }}>
                  <Link to={AppRoutes.LOGIN}>Return to login</Link>
                </Box>
              </form>
              )
        )}
      </Box>
    </Box>
  )
}
